import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useGetAllSectorsNotPaginated } from "../../api/sector";
import { useGetAdminConfigUplifts } from "../../api/price";
import { CLIENT_SHIFT_TYPE_OPTIONS } from "../../constants/clientPortal";
import { formatGbp } from "../../helpers/currency";
import {
  calculateRoleRateCardHourly,
  calculateWorkerRoleRateCardHourly,
} from "../../helpers/roleRateCardPricing";

function extractSectors(data) {
  const payload = data?.data ?? data ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.sectors)) return payload.sectors;
  return [];
}

function SelectionCard({ selected, title, subtitle, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`client-rate-card__choice ${
        selected ? "client-rate-card__choice--selected" : ""
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="client-rate-card__choice-title">{title}</div>
      {subtitle ? (
        <div className="client-rate-card__choice-subtitle">{subtitle}</div>
      ) : null}
    </button>
  );
}

SelectionCard.propTypes = {
  selected: PropTypes.bool,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

const RoleRateCards = ({ t, variant = "client" }) => {
  const isWorker = variant === "worker";

  document.title = isWorker
    ? t("workerRoleRateCardsPageTitle", "Role & Rate Cards | Worker")
    : t("clientRoleRateCardsPageTitle", "Role & Rate Cards | Client");

  const [sectorSlug, setSectorSlug] = useState("");
  const [roleSlug, setRoleSlug] = useState("");
  const [shiftType, setShiftType] = useState("");

  const sectorsQuery = useGetAllSectorsNotPaginated();
  const upliftsQuery = useGetAdminConfigUplifts();

  const sectors = useMemo(
    () => extractSectors(sectorsQuery.data),
    [sectorsQuery.data]
  );

  const selectedSector = useMemo(
    () => sectors.find((s) => s.slug === sectorSlug) || null,
    [sectors, sectorSlug]
  );

  const roles = useMemo(() => {
    const list = selectedSector?.roles;
    return Array.isArray(list) ? list.filter((r) => r?.isActive !== false) : [];
  }, [selectedSector]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.slug === roleSlug) || null,
    [roles, roleSlug]
  );

  const uplifts = useMemo(
    () => upliftsQuery.data?.data ?? upliftsQuery.data ?? null,
    [upliftsQuery.data]
  );

  const rateBreakdown = useMemo(() => {
    if (!selectedRole || !shiftType || !uplifts) return null;
    if (isWorker) {
      return calculateWorkerRoleRateCardHourly({
        workerBaseRate: selectedRole.workerBaseRate,
        shiftType,
        uplifts,
      });
    }
    return calculateRoleRateCardHourly({
      workerBaseRate: selectedRole.workerBaseRate,
      shiftType,
      uplifts,
      marginPercentage: selectedRole.marginPercentage,
    });
  }, [selectedRole, shiftType, uplifts, isWorker]);

  const selectedShiftOption = useMemo(
    () => CLIENT_SHIFT_TYPE_OPTIONS.find((o) => o.value === shiftType) || null,
    [shiftType]
  );

  const isLoading = sectorsQuery.isPending || upliftsQuery.isPending;
  const hasError = sectorsQuery.isError || upliftsQuery.isError;

  const handleSectorSelect = (slug) => {
    setSectorSlug(slug);
    setRoleSlug("");
    setShiftType("");
  };

  const handleRoleSelect = (slug) => {
    setRoleSlug(slug);
    setShiftType("");
  };

  const menuLabel = isWorker
    ? t("workerMenuRoleRateCards", "Role & Rate Cards")
    : t("clientMenuRoleRateCards", "Role & Rate Cards");

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title={menuLabel} breadcrumbItem={menuLabel} />

        <Card className="client-rate-card-hero border-0 mb-4">
          <CardBody className="p-4 p-lg-5">
            <div className="client-rate-card-hero__eyebrow mb-2">
              {t("clientRateCardsEyebrow", "Transparent pricing")}
            </div>
            <h2 className="client-rate-card-hero__title mb-2">
              {isWorker
                ? t(
                    "workerRateCardsTitle",
                    "Explore role rates before you accept work"
                  )
                : t(
                    "clientRateCardsTitle",
                    "Explore role rates before you request staff"
                  )}
            </h2>
            <p className="client-rate-card-hero__copy mb-0">
              {isWorker
                ? t(
                    "workerRateCardsIntro",
                    "Select a sector, choose a role, then pick a shift type to see your hourly pay rate."
                  )
                : t(
                    "clientRateCardsIntro",
                    "Select a sector, choose a role, then pick a shift type to see the hourly charge rate."
                  )}
            </p>
          </CardBody>
        </Card>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : hasError ? (
          <Alert color="danger">
            {t(
              "clientRateCardsLoadError",
              "Unable to load rate card data right now. Please try again later."
            )}
          </Alert>
        ) : (
          <Row className="g-4">
            <Col lg={7}>
              <Card className="client-rate-card-panel border-0 mb-4">
                <CardBody className="p-4">
                  <h5 className="client-rate-card-panel__title">
                    1. {t("clientRateCardsPickSector", "Choose a sector")}
                  </h5>
                  {!sectors.length ? (
                    <p className="text-muted mb-0">
                      {t(
                        "clientRateCardsNoSectors",
                        "No sectors are available."
                      )}
                    </p>
                  ) : (
                    <div className="client-rate-card__grid">
                      {sectors.map((sector) => (
                        <SelectionCard
                          key={sector.slug}
                          selected={sector.slug === sectorSlug}
                          title={sector.title || sector.name || sector.slug}
                          onClick={() => handleSectorSelect(sector.slug)}
                        />
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card className="client-rate-card-panel border-0 mb-4">
                <CardBody className="p-4">
                  <h5 className="client-rate-card-panel__title">
                    2. {t("clientRateCardsPickRole", "Choose a role")}
                  </h5>
                  {!sectorSlug ? (
                    <p className="text-muted mb-0">
                      {t(
                        "clientRateCardsSelectSectorFirst",
                        "Select a sector to see available roles."
                      )}
                    </p>
                  ) : !roles.length ? (
                    <p className="text-muted mb-0">
                      {t(
                        "clientRateCardsNoRoles",
                        "No roles are available for this sector."
                      )}
                    </p>
                  ) : (
                    <div className="client-rate-card__grid">
                      {roles.map((role) => (
                        <SelectionCard
                          key={role.slug}
                          selected={role.slug === roleSlug}
                          title={role.title || role.name || role.slug}
                          onClick={() => handleRoleSelect(role.slug)}
                        />
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card className="client-rate-card-panel border-0 mb-4">
                <CardBody className="p-4">
                  <h5 className="client-rate-card-panel__title">
                    3. {t("clientRateCardsPickShift", "Choose a shift type")}
                  </h5>
                  {!roleSlug ? (
                    <p className="text-muted mb-0">
                      {t(
                        "clientRateCardsSelectRoleFirst",
                        "Select a role to compare shift types."
                      )}
                    </p>
                  ) : (
                    <div className="client-rate-card__grid client-rate-card__grid--shifts">
                      {CLIENT_SHIFT_TYPE_OPTIONS.map((option) => (
                        <SelectionCard
                          key={option.value}
                          selected={option.value === shiftType}
                          title={t(
                            `clientRateCardsShift_${option.value}`,
                            option.label
                          )}
                          onClick={() => setShiftType(option.value)}
                        />
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="client-rate-card-summary border-0 sticky-lg-top">
                <CardBody className="p-4">
                  <h5 className="client-rate-card-panel__title mb-3">
                    {t("clientRateCardsSummary", "Rate summary")}
                  </h5>

                  {!rateBreakdown || !selectedRole || !selectedShiftOption ? (
                    <div className="client-rate-card-summary__empty">
                      <i className="bx bx-calculator" aria-hidden="true" />
                      <p className="mb-0">
                        {t(
                          "clientRateCardsSummaryEmpty",
                          "Complete the steps to see an indicative hourly rate."
                        )}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="client-rate-card-summary__meta mb-3">
                        <Badge className="badge-soft-primary me-1 mb-1">
                          {selectedSector?.title ||
                            selectedSector?.name ||
                            t("clientRateCardsSectorFallback", "Sector")}
                        </Badge>
                        <Badge className="badge-soft-secondary me-1 mb-1">
                          {selectedRole.title || selectedRole.name}
                        </Badge>
                        <Badge className="badge-soft-success mb-1">
                          {t(
                            `clientRateCardsShift_${selectedShiftOption.value}`,
                            selectedShiftOption.label
                          )}
                        </Badge>
                      </div>

                      <p className="text-muted small mb-4">
                        {t(
                          `clientRateCardsShiftDesc_${selectedShiftOption.value}`,
                          selectedShiftOption.description
                        )}
                      </p>

                      <div className="client-rate-card-summary__rows">
                        <div className="client-rate-card-summary__row client-rate-card-summary__row--total">
                          <span>
                            {isWorker
                              ? t(
                                  "workerRateCardsHourlyRate",
                                  "Your indicative hourly rate"
                                )
                              : t(
                                  "clientRateCardsHourlyRate",
                                  "Indicative hourly rate"
                                )}
                          </span>
                          <strong>
                            {t("clientRateCardsPerHour", {
                              rate: formatGbp(rateBreakdown.hourlyRate),
                              defaultValue: "{{rate}} / hr",
                            })}
                          </strong>
                        </div>
                      </div>

                      <Alert color="light" className="mt-4 mb-0 small">
                        {isWorker
                          ? t(
                              "workerRateCardsDisclaimer",
                              "Rates include holiday pay."
                            )
                          : t(
                              "clientRateCardsDisclaimer",
                              "Rates are fully inclusive of applicable employment costs and JobArk’s service charge."
                            )}
                      </Alert>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

RoleRateCards.propTypes = {
  t: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["client", "worker"]),
};

const ClientRoleRateCardsPage = (props) => (
  <RoleRateCards {...props} variant="client" />
);

const WorkerRoleRateCardsPage = (props) => (
  <RoleRateCards {...props} variant="worker" />
);

const ClientRoleRateCards = withTranslation()(ClientRoleRateCardsPage);
const WorkerRoleRateCards = withTranslation()(WorkerRoleRateCardsPage);

export { WorkerRoleRateCards };
export default ClientRoleRateCards;
